import {
  Icon,
  OverflowMenu,
  OverflowMenuItem,
  Table,
  tailwindOverride,
  Typography,
} from '@getsynapse/design-system';
import emptyBudget from 'assets/images/empty-budget-image.svg';
import classnames from 'classnames';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { get } from 'lodash';
import { MouseEvent, useCallback, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import RenderNoRecords from '../../../../Atoms/NoRecords/NoRecords';
import TableSkeletonLoader from '../../../../Organisms/TableSkeletonLoader/TableSkeletonLoader';
import {
  deleteBudget,
  getBudgetListStatus,
  selectListBudget,
} from '../../../../state/BudgetList/budgetListSlice';
import { selectIsUserLd } from '../../../../state/User/userSlice';
import {
  BUDGET_LIST_ACTIONS,
  PATHS,
  SLICE_STATUS,
} from '../../../../utils/constants';
import { BudgetModel } from '../../../../utils/customTypes';
import { numberFormat } from '../../helpers/budgetHelper';
import { getStatusColumn } from '../../helpers/budgetTableHelper';
import { DeleteBudgetModal } from './components/modals/DeleteBudgetModal';
import { DuplicateBudgetModal } from './components/modals/DuplicateBudgetModal';
import { RenameBudgetModal } from './components/modals/RenameBudgetModal';

const BudgetManage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const budgetList = useSelector(selectListBudget);
  const listStatus = useSelector(getBudgetListStatus);
  const isLDUser = useSelector(selectIsUserLd);

  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [displayDuplicateBudgetModal, setDisplayDuplicateBudgetModal] =
    useState(false);
  const [selectedBudgetInfo, setSelectedBudgetInfo] =
    useState<BudgetModel | null>(null);
  const [renameBudgetInfo, setRenameBudgetInfo] = useState<{
    id: string;
    title: string;
  }>();
  const [displayRenameModal, setDisplayRenameModal] = useState(false);

  const {
    isNotificationVisible,
    snackbarProps,
    showNotification,
    SnackbarNotification,
  } = useSnackbarNotification();

  const setBudgetToDelete = async () => {
    if (selectedBudgetInfo == null) {
      return;
    }

    try {
      const data = await dispatch(deleteBudget(selectedBudgetInfo.id));
      if (get(data, 'payload', null)) {
        showNotification({
          notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
          notificationMessage: intl.get(
            'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_DELETION_SUCCESS',
            { title: selectedBudgetInfo.title }
          ),
          hasActionButton: true,
        });
      }
    } catch (error) {
      showNotification({
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR'
        ),
        hasActionButton: false,
      });
    }

    setDisplayDeleteModal(false);
  };

  function formatDate(data: string) {
    const date = new Date(data);
    return Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  function onCreateBudget(data: BudgetModel) {
    try {
      setDisplayDuplicateBudgetModal(false);
      showNotification({
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_SUCCESS',
          { title: data.title }
        ),
        hasActionButton: true,
      });
    } catch (error) {
      showNotification({
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_ERROR',
          { title: data.title }
        ),
        hasActionButton: false,
      });
    }
  }

  const onRenameBudget = useCallback(
    (success: boolean) => {
      if (!success) {
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'BUDGET_PAGE.RENAME_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'BUDGET_PAGE.RENAME_BUDGET_MODAL.NOTIFICATIONS.ERROR_MSG'
          ),
          hasActionButton: false,
        });
      } else {
        setDisplayRenameModal(false);
      }
    },
    [showNotification]
  );

  const getActionsRow = (
    budget: any,
    stickyCellsStyles: (className: string) => string
  ) => {
    const overflowMenuItems: {
      label: string;
      iconName?: string;
      iconSrc?: string;
      onSelect: () => void;
      action: typeof BUDGET_LIST_ACTIONS[keyof typeof BUDGET_LIST_ACTIONS];
    }[] = [
      {
        label: intl.get('BUDGET_PAGE.DELETE_BUDGET_MODAL.TITLE'),
        iconName: 'trash',
        onSelect: () => {
          setSelectedBudgetInfo(budget);
          setDisplayDeleteModal(true);
        },
        action: BUDGET_LIST_ACTIONS.DELETE,
      },
      {
        label: intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.TITLE'),
        iconName: 'copy-outline',
        onSelect: () => {
          setSelectedBudgetInfo(budget);
          setDisplayDuplicateBudgetModal(true);
        },
        action: BUDGET_LIST_ACTIONS.DUPLICATE,
      },
      {
        label: intl.get('BUDGET_PAGE.RENAME_BUDGET_MODAL.TITLE'),
        iconName: 'pencil-outline',
        onSelect: () => {
          setRenameBudgetInfo({ id: budget.id, title: budget.title });
          setDisplayRenameModal(true);
        },
        action: BUDGET_LIST_ACTIONS.RENAME,
      },
    ];

    return [
      {
        content: (
          <div
            className={classnames(
              'h-full text-center relative flex space-x-2',
              {
                'w-12': !(budgetList.length === 0) && isLDUser,
                'w-6': !(budgetList.length === 0) && !isLDUser,
                'w-full': budgetList.length === 0,
              }
            )}
            onClick={(event: MouseEvent<HTMLInputElement>) =>
              event.stopPropagation()
            }
          >
            {isLDUser && (
              <OverflowMenu
                menuListProps={{
                  'data-cy': `budget-${budget.id}__actions-popover`,
                  className: 'shadow-raised',
                }}
                menuButtonProps={{
                  'data-cy': `budget-${budget.id}__actions-popover-trigger`,
                }}
              >
                {overflowMenuItems.map((item) => (
                  <OverflowMenuItem
                    key={item.label}
                    className='group flex items-center space-x-2'
                    onSelect={item.onSelect}
                    data-cy={`budget-${budget.id}__actions-popover__${item.action}`}
                  >
                    <Icon
                      data-cy={`${item.label}-icon`}
                      name={item.iconName || ''}
                      src={item.iconSrc || ''}
                      className={tailwindOverride(
                        'fill-current text-primary',
                        'group-active:text-neutral-white',
                        'text-lg',
                        {
                          'text-xl': item.iconSrc,
                        }
                      )}
                    />
                    <Typography
                      variant='label'
                      data-cy={`${item.label}-label`}
                      className={tailwindOverride(
                        'group-active:text-neutral-white',
                        'cursor-pointer'
                      )}
                    >
                      {item.label}
                    </Typography>
                  </OverflowMenuItem>
                ))}
              </OverflowMenu>
            )}
          </div>
        ),
        className: stickyCellsStyles(
          classnames({
            'w-12': !(budgetList.length === 0) && isLDUser,
            'w-6': !(budgetList.length === 0) && !isLDUser,
            'right-0': !(budgetList.length === 0),
          })
        ),
      },
    ];
  };

  function onSelectRow(budgetInfo: BudgetModel) {
    history.replace(`${PATHS.BUDGETS}/${budgetInfo.id}`);
  }

  return (
    <div className='mt-4' data-cy='budget-list'>
      {listStatus === SLICE_STATUS.LOADING && <TableSkeletonLoader />}
      {listStatus === SLICE_STATUS.IDLE && budgetList.length === 0 && (
        <div className='flex flex-col justify-center items-center gap-y-4 h-100'>
          <div>{intl.get('BUDGET_PAGE.GET_STARTED_CREATE_BUDGET')}</div>
          <RenderNoRecords
            dataCy='empty-budgets-table'
            className='h-auto'
            imageSrc={emptyBudget}
          ></RenderNoRecords>
        </div>
      )}
      {listStatus === SLICE_STATUS.IDLE && budgetList.length > 0 && (
        <Table
          canSelectRows={false}
          className='w-full mb-6'
          data-testid='budgets-table'
          data={{
            total: budgetList.length,
            headData: {
              stickyHeader: true,
              rowProps: {
                className: 'text-end',
              },
              headCells: [
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.NAME'),
                  className: classnames('h-10'),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.YEAR_START'),
                  className: classnames('h-10 w-44 text-end'),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.STATUS'),
                  className: classnames('h-10 w-32 '),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.BUDGET'),
                  className: classnames('h-10 w-56 '),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.ALLOCATED'),
                  className: classnames('h-10 w-56 '),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.ACTUALS'),
                  className: classnames('h-10 w-56 '),
                },
                {
                  content: intl.get('BUDGET_PAGE.YEAR_TABLE.HEAD.OPTIONS'),
                  className: classnames('h-10 w-20 '),
                },
              ],
            },
            rows: budgetList.map((budgetInfo, index) => {
              const isOdd = index % 2;
              const stickyCellsStyles = (className: string) =>
                classnames(
                  'border-transparent',
                  {
                    'sticky z-1': budgetList.length !== 0,
                    'bg-neutral-white': !isOdd,
                    'bg-neutral-lightest-two': isOdd,
                  },
                  className
                );

              return {
                id: `budget-${budgetInfo.id}`,
                className: 'group cursor-pointer',
                onClick: () => {
                  onSelectRow(budgetInfo);
                },
                cells: [
                  {
                    content: <div>{budgetInfo.title}</div>,
                  },
                  {
                    content: (
                      <div>{formatDate(budgetInfo.fiscalPeriodStart)}</div>
                    ),
                  },
                  {
                    content: <div>{getStatusColumn(budgetInfo.status)}</div>,
                  },

                  {
                    content: (
                      <div>
                        {'$' + numberFormat(budgetInfo.totalTarget ?? 0)}
                      </div>
                    ),
                  },
                  {
                    content: (
                      <div>
                        {'$' + numberFormat(budgetInfo.totalAllocated ?? 0)}
                      </div>
                    ),
                  },
                  {
                    content: (
                      <div>
                        {'$' + numberFormat(budgetInfo.totalActuals ?? 0)}
                      </div>
                    ),
                  },
                  ...getActionsRow(budgetInfo, stickyCellsStyles),
                ],
              };
            }),
          }}
        />
      )}

      <DeleteBudgetModal
        isOpen={displayDeleteModal}
        closeModal={() => setDisplayDeleteModal(false)}
        onCancel={() => setDisplayDeleteModal(false)}
        title={selectedBudgetInfo?.title ?? ''}
        onDeleteBudget={() => setBudgetToDelete()}
      />

      {selectedBudgetInfo && (
        <DuplicateBudgetModal
          fromBudget={{
            id: selectedBudgetInfo.id,
            name: selectedBudgetInfo.title,
            year_start: new Date(selectedBudgetInfo.fiscalPeriodStart),
          }}
          isOpen={displayDuplicateBudgetModal}
          closeModal={() => setDisplayDuplicateBudgetModal(false)}
          onCancel={() => setDisplayDuplicateBudgetModal(false)}
          onDuplicateBudget={onCreateBudget}
        />
      )}

      {/*Floating Alert Notification*/}
      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}

      {renameBudgetInfo && (
        <RenameBudgetModal
          id={renameBudgetInfo.id}
          currentName={renameBudgetInfo.title}
          isOpen={displayRenameModal}
          closeModal={() => setDisplayRenameModal(false)}
          onCancel={() => setDisplayRenameModal(false)}
          onRenameBudget={onRenameBudget}
        />
      )}
    </div>
  );
};

export default BudgetManage;
